import { Table, Dropdown } from "@/config/antd";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const RoleManagementTable = ({
  setSelectedRoles,
  data,
  onDelete,
  onModify,
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Return null if there's no data
  if (!data || data.length === 0) {
    return null;
  }

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.description
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.type?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : data;

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRoles(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  const handleMenuClick = ({ key }, record) => {
    if (key === "delete") {
      setSelectedRole(record);
      setIsDeleteModalVisible(true);
    } else if (key === "modify") {
      onModify(record);
    }
  };

  return (
    <>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        locale={{ emptyText: " " }} // Hide default empty state
        rowKey="id">
        <Column
          title="Role"
          key="name"
          dataIndex="name"
          render={(_, { id, name }) => (
            <Link
              to={`/dashboard/roles/${id}`}
              className="flex items-center gap-5 text-sm font-semibold">
              <p className="text-bold font-bold">{name}</p>
            </Link>
          )}
        />
        <Column
          title="Description"
          key="description"
          dataIndex="description"
          render={(_, { id, description }) => (
            <Link
              to={`/dashboard/roles/${id}`}
              className="flex items-center gap-5 text-sm">
              <p className="text-small">{description}</p>
            </Link>
          )}
        />
        <Column
          title="Uploader"
          key="type"
          dataIndex="type"
          render={(_, { id, type }) => (
            <Link
              to={`/dashboard/roles/${id}`}
              className="flex items-center gap-5 text-sm font-semibold">
              <p className="text-small font-bold">{type}</p>
            </Link>
          )}
        />
        <Column
          title="Status"
          key="status"
          dataIndex="status"
          render={(_, { id, status }) => (
            <Link
              to={`/dashboard/roles/${id}`}
              className="flex items-center justify-center p-2 rounded-lg gap-1 bg-[#E5F2FF]">
              <p className="text-[#1CA78B] text-small">{status}</p>
            </Link>
          )}
        />
        <Column
          title=""
          key="options"
          dataIndex="options"
          render={(_, record) => {
            // Default roles are system-defined; hide edit/delete affordances.
            if (record?.type === "Default") {
              return null;
            }

            return (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                    },
                    {
                      key: "delete",
                      label: "Delete",
                    },
                  ],
                  onClick: (e) => handleMenuClick(e, record),
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            );
          }}
        />
      </Table>

      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={() => {
          setIsDeleteModalVisible(false);
          if (selectedRole) {
            onDelete(selectedRole.id, selectedRole.name);
          }
        }}
        header="Delete Role"
        paragraph={
          selectedRole
            ? `Are you sure you want to delete "${selectedRole.name}"?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

RoleManagementTable.propTypes = {
  setSelectedRoles: PropTypes.func,
  data: PropTypes.array,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
};

export default RoleManagementTable;
